<template>
  <div class="country-phone-input-wrapper">
    <div class="input-group cp-phone-group" :class="{ 'has-error': hasError, 'is-focused': isFocused }">
      <!-- Country Dial Code Badge -->
      <div class="input-group-prepend">
        <span class="input-group-text cp-dial-code-badge" :title="meta.countryName">
          <span class="dial-code-text">{{ meta.dialCode }}</span>
        </span>
      </div>

      <!-- Phone Number Input -->
      <input
        :id="id"
        :name="name"
        type="tel"
        class="form-control cp-input cp-phone-input"
        :class="{ 'is-invalid': hasError }"
        :value="value"
        :placeholder="placeholder || meta.placeholder"
        :maxlength="meta.maxLength"
        :disabled="disabled"
        autocomplete="tel-national"
        @input="onInput"
        @paste="onPaste"
        @keypress="onKeyPress"
        @focus="onFocus"
        @blur="onBlur"
      />
    </div>

    <!-- Country Format Guidance Hint -->
    <div v-if="showHint && !hasError" class="phone-hint-text text-white-50 font-size-11 mt-1">
      <i class="fa fa-info-circle me-1 opacity-75"></i>
      <span>{{ meta.countryName }}: {{ meta.hint }}</span>
    </div>

    <!-- Validation Error Feedback -->
    <div v-if="showError && hasError" class="phone-error-msg text-danger font-size-12 mt-1">
      <i class="fa fa-exclamation-circle me-1"></i>
      <span>{{ errorMessage }}</span>
    </div>
  </div>
</template>

<script>
import {
  getPhoneMetadata,
  cleanPhoneNumber,
  validatePhoneNumber
} from "@/common/phone-validation";

export default {
  name: "CountryPhoneInput",
  props: {
    value: {
      type: [String, Number],
      default: ""
    },
    countryCode: {
      type: String,
      default: "US"
    },
    isSubmit: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ""
    },
    showHint: {
      type: Boolean,
      default: true
    },
    showError: {
      type: Boolean,
      default: true
    },
    id: {
      type: String,
      default: "phone_number_input"
    },
    name: {
      type: String,
      default: "phone"
    }
  },
  data() {
    return {
      touched: false,
      isFocused: false
    };
  },
  computed: {
    meta() {
      return getPhoneMetadata(this.countryCode);
    },
    validationResult() {
      return validatePhoneNumber(this.value, this.countryCode);
    },
    hasError() {
      if (!this.showError) return false;
      const valStr = this.value !== null && this.value !== undefined ? String(this.value).trim() : "";
      if (this.isSubmit) {
        return !this.validationResult.isValid;
      }
      if (this.touched && valStr.length > 0) {
        return !this.validationResult.isValid;
      }
      return false;
    },
    errorMessage() {
      return this.validationResult.message || "";
    }
  },
  watch: {
    countryCode(newCode) {
      if (this.value) {
        const cleaned = cleanPhoneNumber(this.value, newCode);
        if (cleaned !== String(this.value)) {
          this.$emit("input", cleaned);
          this.$emit("change", cleaned);
        }
      }
      this.emitValidation();
    },
    value() {
      this.emitValidation();
    }
  },
  mounted() {
    this.emitValidation();
  },
  methods: {
    onInput(event) {
      const raw = event.target.value;
      const cleaned = cleanPhoneNumber(raw, this.countryCode);
      // Force input element to show cleaned/clamped value if user typed over maxlength
      if (event.target.value !== cleaned) {
        event.target.value = cleaned;
      }
      this.$emit("input", cleaned);
      this.$emit("change", cleaned);
    },
    onPaste(event) {
      const clipboardData = event.clipboardData || window.clipboardData;
      if (!clipboardData) return;
      const pasted = clipboardData.getData("text");
      const cleaned = cleanPhoneNumber(pasted, this.countryCode);
      event.preventDefault();
      this.$emit("input", cleaned);
      this.$emit("change", cleaned);
    },
    onKeyPress(event) {
      // Allow control keys (backspace, tab, enter, delete)
      const isControl =
        event.keyCode === 8 ||
        event.keyCode === 9 ||
        event.keyCode === 13 ||
        event.keyCode === 46 ||
        event.ctrlKey ||
        event.metaKey;
      if (isControl) return true;

      // Allow digits only (0-9)
      const charCode = event.which ? event.which : event.keyCode;
      if (charCode < 48 || charCode > 57) {
        event.preventDefault();
        return false;
      }

      // Block keypress if already reached maxLength
      const currentLength = this.value ? String(this.value).length : 0;
      if (currentLength >= this.meta.maxLength && !isControl) {
        event.preventDefault();
        return false;
      }
      return true;
    },
    onFocus() {
      this.isFocused = true;
      this.$emit("focus");
    },
    onBlur() {
      this.isFocused = false;
      this.touched = true;
      this.$emit("blur");
      this.emitValidation();
    },
    emitValidation() {
      this.$emit("validation", this.validationResult);
    }
  }
};
</script>

<style scoped>
.country-phone-input-wrapper {
  position: relative;
  width: 100%;
}

.cp-phone-group {
  display: flex;
  align-items: stretch;
  width: 100%;
}

.cp-dial-code-badge {
  background: rgba(255, 255, 255, 0.08) !important;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  border-right: none !important;
  border-top-left-radius: 12px !important;
  border-bottom-left-radius: 12px !important;
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  color: #ffffff !important;
  font-weight: 600 !important;
  font-size: 13.5px !important;
  padding: 0 14px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 58px;
  height: 46px !important;
  letter-spacing: 0.3px;
  transition: border-color 0.25s ease, box-shadow 0.25s ease;
  user-select: none;
}

.cp-phone-input {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
  border-top-right-radius: 12px !important;
  border-bottom-right-radius: 12px !important;
  border-left: 1px solid rgba(255, 255, 255, 0.08) !important;
  height: 46px !important;
  font-size: 14px !important;
  background: rgba(255, 255, 255, 0.06) !important;
  color: #ffffff !important;
  transition: border-color 0.25s ease, box-shadow 0.25s ease;
}

.cp-phone-group.is-focused .cp-dial-code-badge {
  border-color: #e53bb9 !important;
}

.cp-phone-group.has-error .cp-dial-code-badge {
  border-color: rgba(255, 77, 79, 0.8) !important;
}

.cp-phone-group.has-error .cp-phone-input {
  border-color: rgba(255, 77, 79, 0.8) !important;
  box-shadow: 0 0 10px rgba(255, 77, 79, 0.25) !important;
}

.phone-hint-text {
  color: rgba(255, 255, 255, 0.55);
  letter-spacing: 0.1px;
}

.phone-error-msg {
  color: #ff6b72 !important;
  font-weight: 500;
  animation: fadeInDown 0.2s ease-out;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-3px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
